/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import _cloneDeep from 'lodash/cloneDeep';
import { Loader } from '@pwc/pwc-deals-ui';
import { Constants } from '@pwc/pwc-deals-web-common';
import { DragDropContext } from 'react-beautiful-dnd';
import { showModalError } from '../../../../common/redux/errors/errorsSlice';
import { getAnalysisForKFA } from '../../services/groupsService.ts';
import AutoLock from '../../../../common/components/AutoLock';
import {
  deleteSingleAnalysis,
  getSelectedIds,
  clearGroupsSelection,
  deselectAllAnalyses,
} from '../../utils/groupsUtilities.ts';
import {
  determineDragSubType,
  determineDragType,
  updateSingleDragInsideOneGroup,
  updateMultiDragInsideOneGroup,
  saveMultipleAnalyses,
  saveSingleAnalyses,
} from '../../utils/dragAndDropUtilities.ts';
import AnalysesKfaList from '../AnalysesKfaList/AnalysesKfaList';
import EmptyMessage from '../EmptyMessage/index.tsx';
import GroupsModal from '../GroupsModal/index.tsx';
import './AnalysesKfaGroupList.scss';

const AnalysesKfaGroupList = ({
  groups = [],
  setGroups,
  analysisCount,
  setAnalysisCount,
  groupsLoading,
  tags,
  permissions,
  enableDragAndDrop = true,
}) => {
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const [sourceId, setSourceId] = useState();
  const [dragging, setDragging] = useState(false);
  const [selectedIDs, setSelectedIDs] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(undefined);

  const [modal, setModal] = useState({
    show: false,
  });
  const [showDeleteAnalysisConfirmation, setShowDeleteAnalysisConfirmation] =
    useState(false);

  const displayErrorModal = (modalErorr) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    dispatch(showModalError(modalErorr));
  };

  const displayOrderErrorMessage = () => {
    displayErrorModal(Constants.ModalErrorType.UNABLE_TO_SAVE_SORT_ORDER);
  };

  const displayChangeErrorMessage = () => {
    displayErrorModal(Constants.ModalErrorType.UNABLE_TO_SAVE_CHANGE);
  };

  const setItemsForGroup = (contentId, items) => {
    const groupsTemp = _cloneDeep(groups);
    for (let i = 0; i < groupsTemp.length; i += 1) {
      if (groupsTemp[i].contentId === contentId) {
        groupsTemp[i].items = items;
      }
    }
    setGroups(groupsTemp);
  };

  const handleDeleteAnalysis = async (contentThreadId) => {
    const updatedGroups = await deleteSingleAnalysis(
      projectId,
      groups,
      contentThreadId
    );
    if (updatedGroups) {
      setGroups(updatedGroups);
      setAnalysisCount(analysisCount - 1);
      setModal({ show: false });
    }
  };

  const showDeleteAnalysisDialog = (id) => {
    setModal({
      modalActionType: 'AnalysesDelete',
      show: true,
      onCancel: () => {
        setModal({ show: false });
      },
      onConfirm: async () => {
        setModal({ show: false });
        await handleDeleteAnalysis(id);
      },
    });
  };

  const handleBeforeDragStart = (ev) => {
    const { source } = ev;
    const { droppableId } = source;
    setSourceId(droppableId);
  };

  const handleDragStart = () => {
    setDragging(true);
  };

  const deselectAll = (groupsToDeselect) => {
    const updatedGroups = clearGroupsSelection(groupsToDeselect);
    setGroups(updatedGroups);
    setSelectedIDs([]);
  };

  const handleDragEnd = async (ev) => {
    if (dragging) {
      setDragging(false);
    }
    const { destination } = ev;

    if (!destination) return;

    const dragType = determineDragType(ev);
    const dragSubType = determineDragSubType(selectedIDs, ev, groups);

    const oldGroups = _cloneDeep(groups);

    if (dragType === 'insideOneGroup') {
      if (dragSubType === 'singledrag') {
        const {
          updatedGroups,
          destGroupId,
          destinationIndex,
          contentThreadIds,
        } = updateSingleDragInsideOneGroup(groups, ev);
        setGroups(updatedGroups);
        const success = await saveSingleAnalyses(
          projectId,
          oldGroups,
          contentThreadIds,
          destGroupId,
          destinationIndex,
          true
        );
        if (!success) {
          displayOrderErrorMessage();
          setGroups(groups);
        } else {
          deselectAll(updatedGroups);
        }
      }
      if (dragSubType === 'multidrag') {
        const updatedMultiDrag = updateMultiDragInsideOneGroup(groups, ev);
        setGroups(updatedMultiDrag.updatedGroups);
        const success = await saveMultipleAnalyses(
          projectId,
          oldGroups,
          selectedIDs,
          updatedMultiDrag.destGroupId,
          updatedMultiDrag.destinationIndex,
          true
        );
        if (!success) {
          setGroups(groups);
          displayOrderErrorMessage();
        } else {
          deselectAll(updatedMultiDrag.updatedGroups);
        }
      }
    }
  };

  const handleSelectItem = (analysisId, selected, groupId) => {
    if (selectedGroupId !== undefined && selectedGroupId !== groupId) {
      const { updatedGroups: unselectedGroups } = deselectAllAnalyses(groups);
      const { updatedGroups, updatedSelectedIDs } = getSelectedIds(
        unselectedGroups,
        analysisId,
        selected
      );
      setGroups(updatedGroups);
      setSelectedIDs([analysisId]);
      setSelectedGroupId(groupId);
    } else {
      const { updatedGroups, updatedSelectedIDs } = getSelectedIds(
        groups,
        analysisId,
        selected
      );
      setGroups(updatedGroups);
      setSelectedIDs(updatedSelectedIDs);
      setSelectedGroupId(groupId);
    }
  };

  const ids = groups.reduce((acc, group) => {
    group.items.forEach((item) => {
      acc.push(item.contentThreadId);
    });
    return acc;
  }, []);
  return (
    <div className="analyses-kfa-view-container">
      {groupsLoading && <Loader />}
      {!groupsLoading && (
        <EmptyMessage
          groupType="kfa"
          groupsCount={groups.length}
          analysisCount={analysisCount}
        />
      )}
      {!groupsLoading && (
        <div>
          <AutoLock projectId={projectId} contentThreadIds={ids}>
            {modal.show && <GroupsModal {...modal} />}
            <DragDropContext
              onBeforeDragStart={handleBeforeDragStart}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
            >
              {groups.map((group) => (
                <AnalysesKfaList
                  key={`${group.title}-${group.id}`}
                  projectId={projectId}
                  group={group}
                  list={group.items}
                  setList={setItemsForGroup}
                  loadPaginatedData={getAnalysisForKFA}
                  handleDeleteAnalyses={showDeleteAnalysisDialog}
                  handleSelect={handleSelectItem}
                  tags={tags}
                  dragInProgress={dragging}
                  sourceId={sourceId}
                  permissions={permissions}
                  selectedIDs={selectedIDs}
                  enableDragAndDrop={enableDragAndDrop}
                />
              ))}
            </DragDropContext>
          </AutoLock>
        </div>
      )}
      {groupsLoading && <></>}
    </div>
  );
};

export default AnalysesKfaGroupList;
